// src/utils/mediumCategory.js
// =====================================================
// MOTEUR DE CATÉGORISATION DES MÉDIUMS — 
// Utilisé par : ArtworkDetailTab, ArtworksDashboard,
//               ImportCSVPremium, ImportSpreadsheet,
//               CollectionBreakdown, migration script
// =====================================================

export const MEDIUM_CATEGORIES = [
  { value: "painting",      label: "Peinture" },
  { value: "photography",   label: "Photographie" },
  { value: "prints",        label: "Gravure / Estampe" },
  { value: "sculpture",     label: "Sculpture" },
  { value: "work_on_paper", label: "Travail sur papier" },
  { value: "drawing",       label: "Dessin" },
  { value: "design",        label: "Design" },
  { value: "installation",  label: "Installation" },
  { value: "film_video",    label: "Film / Vidéo" },
  { value: "nft",           label: "Art numérique (NFT, vidéo, génératif)" },
  { value: "textile",       label: "Textile / Tapisserie" },
  { value: "mixed_media",   label: "Technique mixte" },
  { value: "ceramics",      label: "Céramique" },
  { value: "other",         label: "Autre" },
];

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

// Ordre important:
// - catégories spécifiques d'abord
// - painting à la fin pour éviter qu'elle avale tout
const RULES = [
  {
    category: "photography",
    keywords: [
      "tirage",
      "tirage argentique",
      "argentique",
      "photographie",
      "photography",
      "photograph",
      "photo ",
      "c-print",
      "c print",
      "chromogenic print",
      "chromogene",
      "chromogene print",
      "chromogene sur papier",
      "chromogène",
      "lambda",
      "cibachrome",
      "ilfochrome",
      "gelatin silver",
      "gelatin silver print",
      "silver gelatin",
      "silver print",
      "pigment print",
      "archival pigment print",
      "pigmentaire",
      "inkjet",
      "jet d'encre",
      "jet d’encre",
      "dye transfer",
      "fuji crystal archive",
      "polaroid",
      "photogram",
      "daguerreotype",
      "daguerreotype",
      "daguerreotype sur plaque",
      "daguerreotype print",
      "daguerréotype",
      "calotype",
      "digital c-print",
    ],
  },
  {
    category: "prints",
    keywords: [
      "print",
      "prints",
      "edition",
      "edition de",
      "edition of",
      "ed.",
      "ed ",
      "lithograph",
      "lithographie",
      "serigraph",
      "serigraphie",
      "sérigraphie",
      "screenprint",
      "screen print",
      "silkscreen",
      "etching",
      "eau-forte",
      "eau forte",
      "aquatint",
      "aquatinte",
      "engraving",
      "gravure",
      "woodcut",
      "xylographie",
      "linocut",
      "linogravure",
      "monotype",
      "monoprint",
      "drypoint",
      "pointe seche",
      "pointe sèche",
      "burin",
      "mezzotint",
      "heliogravure",
      "héliogravure",
      "offset",
      "estampe",
      "taille douce",
      "taille-douce",
      "multiple",
    ],
  },
  {
    category: "work_on_paper",
    keywords: [
      "work on paper",
      "works on paper",
      "sur papier",
      "on paper",
      "papier",
      "paper",
      "papier japon",
      "papier velin",
      "papier vélin",
      "papier chiffon",
      "papier maroufle",
      "papier marouflé",
      "cardboard",
      "carton",
      "paper pulp",
    ],
  },
  {
    category: "textile",
    keywords: [
      "textile",
      "textiles",
      "textile art",
      "fiber art",
      "fibre",
      "tapisserie",
      "broderie",
      "embroidery",
      "tissage",
      "weaving",
      "woven",
      "laine",
      "wool",
      "soie",
      "silk",
      "crochet",
      "knit",
      "tricot",
      "tissu",
      "fabric",
      "felt",
      "dentelle",
      "lace",
      "quilting",
      "patchwork",
    ],
  },
  {
    category: "ceramics",
    keywords: [
      "ceramic",
      "ceramics",
      "ceramique",
      "céramique",
      "porcelain",
      "porcelaine",
      "stoneware",
      "gres",
      "grès",
      "earthenware",
      "faience",
      "faïence",
      "terracotta",
      "terre cuite",
      "clay",
      "argile",
      "glazed ceramic",
      "raku",
    ],
  },
  {
    category: "sculpture",
    keywords: [
      "sculpture",
      "bronze",
      "marble",
      "marbre",
      "resin",
      "resine",
      "résine",
      "plaster",
      "platre",
      "plâtre",
      "stone",
      "pierre",
      "steel",
      "acier",
      "aluminum",
      "aluminium",
      "iron",
      "fonte",
      "wax",
      "cire",
      "glass",
      "verre",
      "blown glass",
      "verre souffle",
      "verre soufflé",
      "wood sculpture",
      "volume",
      "modelage",
      "taille directe",
      "assemblage",
    ],
  },
  {
    category: "design",
    keywords: [
      "design",
      "collectible design",
      "design object",
      "objet design",
      "furniture",
      "mobilier",
      "chair",
      "chaise",
      "table",
      "lamp",
      "lampe",
      "console",
      "cabinet",
      "fauteuil",
    ],
  },
  {
    category: "painting",
    keywords: [
      "painting",
      "paint",
      "peinture",
      "oil",
      "oil on canvas",
      "huile",
      "huile sur toile",
      "huile sur panneau",
      "huile sur bois",
      "acrylic",
      "acrylique",
      "watercolor",
      "watercolour",
      "aquarelle",
      "gouache",
      "tempera",
      "encaustic",
      "encaustique",
      "fresco",
      "fresque",
      "lacquer",
      "laque",
      "alkyd",
      "alkyde",
      "pastel a l'huile",
      "pastel à l'huile",
      "pastel gras",
      "pigment on canvas",
      "sur toile",
      "sur bois",
      "sur panneau",
      "sur carton",
    ],
  },
];

export function inferMediumCategory(mediumText) {
  if (!mediumText || typeof mediumText !== "string") return "other";

  const m = normalizeText(mediumText);

  for (const rule of RULES) {
    for (const kw of rule.keywords) {
      if (m.includes(normalizeText(kw))) {
        return rule.category;
      }
    }
  }

  return "other";
}

export function getMediumCategoryLabel(categoryValue) {
  const found = MEDIUM_CATEGORIES.find((c) => c.value === categoryValue);
  return found ? found.label : categoryValue || "—";
}